import * as THREE from "three";
import { activeCanvasPointers, type InputState } from "./input";

const LOOK_SENSITIVITY = 0.0038;
const MIN_PITCH = -1.54;
const MAX_PITCH = 1.45;
const MIN_DIST = 1.2;
const MAX_DIST = 2500;
const MIN_HEIGHT = 0.7;
const MIN_POLAR = 0.06;
const FALLBACK_DIST = 24;
const MAX_PIVOT_DIST = 480;
const GROUND_Y = 0.05;
const BASE_SPEED = 18;
const SPRINT = 2.6;

export class FlyCamera {
  readonly camera: THREE.PerspectiveCamera;
  enabled = true;
  yaw = 0;
  pitch = -0.62;

  private keys = new Set<string>();
  private canvas: HTMLCanvasElement;
  private velocity = new THREE.Vector3();
  private input: InputState;
  private homePos = new THREE.Vector3();
  private homeYaw = 0;
  private homePitch = 0;
  private homeFov = 52;
  private pivot = new THREE.Vector3();
  private hasPivot = false;
  private offset = new THREE.Vector3();
  private spherical = new THREE.Spherical();
  private lookDir = new THREE.Vector3();
  private scratch = new THREE.Vector3();

  constructor(canvas: HTMLCanvasElement, camera: THREE.PerspectiveCamera, input: InputState) {
    this.canvas = canvas;
    this.camera = camera;
    this.input = input;
    this.camera.rotation.order = "YXZ";
    this.bind();
  }

  lookAt(target: THREE.Vector3): void {
    const dx = target.x - this.camera.position.x;
    const dy = target.y - this.camera.position.y;
    const dz = target.z - this.camera.position.z;
    this.yaw = Math.atan2(-dx, -dz);
    this.pitch = Math.atan2(dy, Math.hypot(dx, dz));
    this.pitch = Math.min(MAX_PITCH, Math.max(MIN_PITCH, this.pitch));
    this.applyRotation();
    this.fitClip();
  }

  captureHome(): void {
    this.homePos.copy(this.camera.position);
    this.homeYaw = this.yaw;
    this.homePitch = this.pitch;
    this.homeFov = this.camera.fov;
  }

  resetHome(): void {
    this.releasePivot();
    this.camera.position.copy(this.homePos);
    this.yaw = this.homeYaw;
    this.pitch = this.homePitch;
    this.camera.fov = this.homeFov;
    this.camera.updateProjectionMatrix();
    this.applyRotation();
    this.fitClip();
  }

  lookBy(dx: number, dy: number): void {
    if (!this.enabled) return;
    this.releasePivot();
    this.yaw += dx * LOOK_SENSITIVITY;
    this.pitch -= dy * LOOK_SENSITIVITY;
    this.pitch = Math.min(MAX_PITCH, Math.max(MIN_PITCH, this.pitch));
    this.applyRotation();
  }

  /** Two-finger drag: orbit around the point currently in the middle of the view. */
  orbitBy(dx: number, dy: number): void {
    if (!this.enabled) return;
    this.ensurePivot();
    this.readSpherical();
    const h = Math.max(1, this.canvas.clientHeight);
    this.spherical.theta -= (2 * Math.PI * dx) / h;
    this.spherical.phi -= (2 * Math.PI * dy) / h;
    this.writeSpherical();
  }

  /** Two-finger twist: spin heading around the current view center. */
  rotateBy(angle: number): void {
    if (!this.enabled || angle === 0) return;
    this.ensurePivot();
    this.readSpherical();
    this.spherical.theta += angle;
    this.writeSpherical();
  }

  pinchDolly(scale: number): void {
    if (!this.enabled || scale <= 0) return;
    this.ensurePivot();
    this.dollyBy(Math.pow(scale, 1.55));
  }

  /** Pinch / wheel / HUD: dolly toward or away from the view center. */
  dollyBy(scale: number): void {
    if (!this.enabled || scale <= 0) return;
    const pivot = this.hasPivot ? this.pivot : this.viewCenter(this.scratch);
    this.offset.copy(this.camera.position).sub(pivot);
    const dist = Math.max(MIN_DIST, this.offset.length());
    const next = clamp(dist / scale, MIN_DIST, MAX_DIST);
    if (this.offset.lengthSq() < 1e-8) {
      this.camera.getWorldDirection(this.lookDir);
      this.offset.copy(this.lookDir).multiplyScalar(-next);
    } else {
      this.offset.multiplyScalar(next / dist);
    }
    this.camera.position.copy(pivot).add(this.offset);
    if (this.camera.position.y < MIN_HEIGHT) this.camera.position.y = MIN_HEIGHT;
    this.lookAt(pivot);
  }

  releasePivot(): void {
    this.hasPivot = false;
  }

  private bind(): void {
    const c = this.canvas;

    c.addEventListener(
      "wheel",
      (e) => {
        if (!this.enabled) return;
        e.preventDefault();
        // Chrome on Android turns two-finger pan/pinch into wheel events.
        if (activeCanvasPointers() > 0) return;
        this.dollyBy(Math.exp(-e.deltaY * 0.01));
      },
      { passive: false },
    );

    window.addEventListener("keydown", (e) => {
      if (!this.enabled) return;
      this.keys.add(e.code);
    });
    window.addEventListener("keyup", (e) => {
      this.keys.delete(e.code);
    });
    window.addEventListener("blur", () => this.keys.clear());
  }

  private applyRotation(): void {
    this.camera.rotation.set(this.pitch, this.yaw, 0, "YXZ");
  }

  update(dt: number): void {
    if (!this.enabled) return;
    const right = this.keys.has("KeyD") || this.keys.has("ArrowRight") ? 1 : 0;
    const left = this.keys.has("KeyA") || this.keys.has("ArrowLeft") ? 1 : 0;
    const forward = this.keys.has("KeyW") || this.keys.has("ArrowUp") ? 1 : 0;
    const back = this.keys.has("KeyS") || this.keys.has("ArrowDown") ? 1 : 0;
    const up = this.keys.has("KeyE") || this.keys.has("Space") ? 1 : 0;
    const down = this.keys.has("KeyQ") || this.keys.has("ControlLeft") ? 1 : 0;

    const x = clamp(this.input.analogX + right - left, -1, 1);
    const y = clamp(this.input.analogY + up - down, -1, 1);
    const z = clamp(this.input.analogZ + back - forward, -1, 1);
    const len = Math.hypot(x, y, z);
    if (len < 0.001) {
      this.fitClip();
      return;
    }

    const sprint =
      this.keys.has("ShiftLeft") ||
      this.keys.has("ShiftRight") ||
      this.input.sprint;
    const speed = BASE_SPEED * (sprint ? SPRINT : 1);
    this.velocity.set(x, y, z).multiplyScalar((speed * dt) / len * Math.min(1, len));
    this.camera.translateX(this.velocity.x);
    this.camera.translateY(this.velocity.y);
    this.camera.translateZ(this.velocity.z);
    if (this.camera.position.y < MIN_HEIGHT) this.camera.position.y = MIN_HEIGHT;
    this.fitClip();
  }

  private ensurePivot(): void {
    if (this.hasPivot) return;
    this.viewCenter(this.pivot);
    this.hasPivot = true;
  }

  private readSpherical(): void {
    this.offset.copy(this.camera.position).sub(this.pivot);
    if (this.offset.lengthSq() < 1e-8) {
      this.camera.getWorldDirection(this.lookDir);
      this.offset.copy(this.lookDir).multiplyScalar(-FALLBACK_DIST);
    }
    this.spherical.setFromVector3(this.offset);
  }

  private writeSpherical(): void {
    this.spherical.radius = clamp(this.spherical.radius, MIN_DIST, MAX_DIST);
    this.spherical.makeSafe();
    const maxPhi = Math.acos(clamp(MIN_HEIGHT / this.spherical.radius, 0.02, 0.98));
    this.spherical.phi = clamp(this.spherical.phi, MIN_POLAR, maxPhi);
    this.offset.setFromSpherical(this.spherical);
    this.camera.position.copy(this.pivot).add(this.offset);
    if (this.camera.position.y < MIN_HEIGHT) this.camera.position.y = MIN_HEIGHT;
    this.lookAt(this.pivot);
  }

  /** Point in the middle of the view: ground hit along the look ray, else a point ahead. */
  private viewCenter(out: THREE.Vector3): THREE.Vector3 {
    this.camera.getWorldDirection(this.lookDir);
    const pos = this.camera.position;
    if (this.lookDir.y < -0.02) {
      const t = (GROUND_Y - pos.y) / this.lookDir.y;
      if (t > 1.2 && t < MAX_PIVOT_DIST) {
        return out.copy(pos).addScaledVector(this.lookDir, t);
      }
    }
    const dist = clamp(Math.abs(pos.y) * 2.4, FALLBACK_DIST, 90);
    return out.copy(pos).addScaledVector(this.lookDir, dist);
  }

  private fitClip(): void {
    const reach = this.camera.position.length() + 420;
    const near = Math.max(0.12, reach * 0.00025);
    const far = Math.max(800, reach * 4);
    if (Math.abs(this.camera.near - near) < 0.02 && Math.abs(this.camera.far - far) < 1) return;
    this.camera.near = near;
    this.camera.far = far;
    this.camera.updateProjectionMatrix();
  }
}

function clamp(n: number, a: number, b: number): number {
  return Math.min(b, Math.max(a, n));
}
